import dayjs from "dayjs";
import { useContext } from "react";
import { Button } from "../components/BaseComponents";
import { markLogoutNotConfirmed } from "../lib/utils";
import usePublicApi from "../hooks/usePublicApi";
import { performLogoutRequest } from "../lib/logout";
import { useNavigate } from "react-router-dom";
import routes from "../components/Router/routes";
import { userContext } from "../context/UserContext";
import useConnectionStatus from "../hooks/useConnectionStatus";
import useReconnectProbe from "../hooks/useReconnectProbe";
import ConnectionStatusBanner from "../components/ConnectionStatusBanner/ConnectionStatusBanner";
import ConnectionGuard from "../components/ConnectionGuard/ConnectionGuard";

type TrainingNotStartedProps = {
    startDate?: string;
};

// Decision 11 (docs/todo/admin-user-training-sessions.md): shown instead of the app for a
// training user whose session hasn't reached its startDate yet.
function TrainingNotStarted({ startDate }: TrainingNotStartedProps) {
    const publicApi = usePublicApi();
    const navigate = useNavigate();
    const { clearUserData } = useContext(userContext);
    const isConnected = useConnectionStatus();
    // Issue #857 (item 4): UserContext.tsx short-circuits to this page instead of mounting Router
    // for a signed-in training user whose session hasn't started, so this is the only
    // probe/banner/guard for that branch - mirrors Router.tsx exactly. Exactly one of Router /
    // OrganisationPickerLayout / VerifyEmailRequired / TrainingNotStarted is ever mounted at a
    // time (UserContext.tsx's short-circuit returns are mutually exclusive), so there is no risk
    // of a duplicate probe.
    const shouldBlockForConnection = !isConnected;
    useReconnectProbe(isConnected);

    const onLogout = async () => {
        try {
            // Issue #759 (final review F1): the CSRF fetch and the POST are both bounded by the
            // same timeout inside performLogoutRequest - see lib/logout.ts.
            await performLogoutRequest(publicApi);
        } catch (err: any) {
            // Issue #759: best-effort - the server-side session will also expire on its own, but
            // the client-side state below is the actual data-leak boundary and must always be
            // cleared, even if this call fails (offline, CSRF mismatch, 5xx, or a request that
            // never settled at all - see withTimeout above).
            console.error('Failed to notify the server of logout, status:', err?.status)
            markLogoutNotConfirmed();
        } finally {
            // Issue #759 (final review F3): clearUserData() already flips userData to undefined,
            // which alone makes Router.tsx redirect to login on the next render (see
            // UserContext.tsx) - a window.location.reload() here was not just redundant, it was
            // actively risky: if the server-side session cookie is still considered valid for any
            // reason (the logout POST above failed), a full reload could silently re-authenticate
            // the previous session instead of landing the user on the login screen.
            clearUserData();
            navigate(routes.publicLogin.path);
        }
    }

    return (
        // This page renders outside AppRouter's flex wrapper (UserContext short-circuits before
        // it mounts), so it's the sole block-level child of #root. Without the flex container
        // below, the inner div's mt-16 collapses through #root's own top margin, pushing #root's
        // background down and exposing the browser's default white body background above it.
        <div className="flex flex-col items-center min-h-dvh">
            {/* Issue #857 (item 4): wrapped in w-full since the outer wrapper's items-center
                otherwise shrinks the banner to its content width, unlike Router.tsx's app-shell
                which stretches children full-width by default. */}
            <div className="w-full"><ConnectionStatusBanner /></div>
            {/* Issue #857 (item 4): ConnectionGuard renders exactly one wrapping div, so passing
                the pre-existing .content classes here (plus relative, needed for the overlay's
                absolute inset-0 positioning) preserves the original DOM depth/layout - see this
                file's own "wraps its content in a flex container" structural test for why that
                matters. Log out is deliberately rendered as a sibling below, outside the
                guarded/inert region, so it stays reachable even while the rest of this page is
                hidden. */}
            <ConnectionGuard block={shouldBlockForConnection} className="content w-[32rem] mt-16 text-center relative">
                <div className="text-charcoal-100 text-2xl">Training has not started yet</div>
                <div className="mt-8 text-charcoal-100">
                    <div>Your training session has not started yet.</div>
                    {startDate && (
                        <div className="mt-4">
                            Please check back after <span>{dayjs(startDate).format('DD MMMM YYYY, HH:mm')}</span>.
                        </div>
                    )}
                </div>
            </ConnectionGuard>
            <Button type="Primary" text="Log out" additionalClass="mt-8" onClick={onLogout} />
        </div>
    );
}

export default TrainingNotStarted;
